'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const coreServices = [
  {
    tag: '[01]',
    title: 'AppSec & Vulnerability Management',
    subtitle: 'SAST · DAST · SCA · CVE Triage',
    description:
      'SAST, DAST, and SCA embedded into your CI/CD pipelines. CVE triage and enrichment driven by business context. Business-logic vulnerabilities that automated scanners miss.',
    href: '/services/appsec',
    span: 'lg:col-span-2',
  },
  {
    tag: '[02]',
    title: 'Compliance & Continuous Compliance',
    subtitle: 'SOC2 · ISO 27001 · DORA · GDPR',
    description:
      'Engineering-led compliance programmes that do not end at certification. Continuous evidence collection, control monitoring, and embedded support through every audit window.',
    href: '/services/compliance',
    span: '',
  },
  {
    tag: '[03]',
    title: 'Threat Modelling & Security Architecture',
    subtitle: 'Risk-led · STRIDE · MITRE ATT&CK',
    description:
      'Security flaws found in design cost a fraction of those found in production. Threat modelling embedded into your engineering process, before code is written.',
    href: '/services/threat-modelling',
    span: '',
  },
  {
    tag: '[04]',
    title: 'Continuous DevSecOps',
    subtitle: 'Embedded · Ongoing · Year-round',
    description:
      'Senior TechSonar engineers working inside your team, in your sprints, your standups, your delivery process. Security at engineering speed, 365 days a year.',
    href: '/services/devsecops',
    span: 'lg:col-span-2',
  },
  {
    tag: '[05]',
    title: 'IaC & Pipeline Security',
    subtitle: 'Terraform · Pulumi · CloudFormation',
    description:
      'Misconfigured infrastructure caught before it is ever provisioned. Policy-as-code enforcement, secrets detection, SBOM generation, and supply chain security.',
    href: '/services/iac-pipeline',
    span: '',
  },
  {
    tag: '[06]',
    title: 'Cloud & Platform Engineering',
    subtitle: 'AWS · Azure · GCP · Kubernetes',
    description:
      'Secure cloud architectures designed from the ground up. IAM hardening, network segmentation, container security, and cloud-native security controls across all major providers.',
    href: '/services/cloud-platform',
    span: '',
  },
];

const pentestingServices = [
  {
    tag: '[07]',
    title: 'Penetration Testing',
    subtitle: 'Web · API · Infrastructure · Mobile',
    description:
      'Senior-led manual penetration testing across web applications, APIs, infrastructure, and mobile. Business-logic vulnerabilities and chained exploits that automated scanners consistently miss.',
    href: '/services/pentesting',
  },
  {
    tag: '[08]',
    title: 'AI Penetration Testing',
    subtitle: 'LLM · RAG · Agentic · Model security',
    description:
      'Adversarial security testing of AI and ML systems, prompt injection, RAG pipeline attacks, model extraction, and agentic AI exploitation. Our specialisation for organisations building or deploying AI.',
    href: '/services/ai-pentesting',
    specialism: true,
  },
];

const programmes = [
  {
    tag: '[A]',
    title: 'Accelerator Programme',
    subtitle: 'Audit-ready · Fast · Embedded',
    description:
      'When you need to get your security posture up to speed quickly, ahead of an audit, a funding round, or a major customer onboarding. Senior engineers embedded directly into your organisation, doing the work at pace. Typically 6–12 weeks, fully scoped to your situation.',
    href: '/services/accelerator',
    callout: true,
  },
  {
    tag: '[B]',
    title: 'Bespoke Security Programmes',
    subtitle: 'Custom · Framework-mapped · Durable',
    description:
      'Not generic frameworks, security programmes designed specifically for your organisation, your technology, and your risk profile. Mapped to SOC2, ISO 27001, DORA, GDPR, and more.',
    href: '/services/security-programmes',
  },
  {
    tag: '[C]',
    title: 'Security Focus Programme',
    subtitle: 'Auditor aligned · Framework mapped · No unnecessary work',
    description:
      'Too many findings, too many frameworks, not enough clarity. We build a prioritised, framework-aligned programme that cuts the unnecessary work and focuses your team on the controls that satisfy auditors.',
    href: '/services/security-focus',
  },
  {
    tag: '[D]',
    title: 'IaC Security Programme',
    subtitle: 'Policy-as-code · Supply chain · Drift governance',
    description:
      'A structured, organisation-wide IaC security programme covering policy-as-code, supply chain security, secrets governance, and drift detection, embedded into your pipelines and your team.',
    href: '/services/iac-programme',
  },
];

export default function ServicesPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Services</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [What we do]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Impactful security across<br />
              <span className="text-[#80edfa]">every layer of your stack.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              From AppSec and CVE triage to continuous compliance, threat modelling, penetration testing, IaC security, and cloud platform engineering, TechSonar delivers security that makes a real difference. Embedded, continuous, and led by senior engineers who understand both security and how modern software is built.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services Bento Grid */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">Core services</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {coreServices?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className={`group bg-[#050a0f] p-8 lg:p-10 flex flex-col justify-between min-h-[280px] hover:bg-[#0a0f14] transition-colors duration-300 fade-in-up ${s?.span}`}
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">{s?.tag}</p>
                    <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 transform" viewBox="0 0 16 16" fill="none">
                      <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 className="font-heading text-xl lg:text-2xl text-[#f5f5f8] font-medium mb-2 group-hover:text-[#80edfa] transition-colors duration-300">{s?.title}</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">{s?.subtitle}</p>
                  <p className="text-sm text-[#666] leading-relaxed group-hover:text-[#aaa] transition-colors duration-300">{s?.description}</p>
                </div>
                <div className="mt-8 flex items-center gap-2">
                  <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Learn more</span>
                  <svg className="w-3 h-3 text-[#444] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            ))}
          </div>

          {/* Pentesting section */}
          <div className="mt-16 mb-12 fade-in-up">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#80edfa] animate-pulse" />
              <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Penetration Testing</p>
            </div>
            <p className="text-[#555] text-sm max-w-[560px]">Manual, senior-led penetration testing, including our specialisation in AI system security testing.</p>
          </div>

          {/* Pentesting callout, full width */}
          <div className="mb-px">
            <Link
              href="/services/pentesting"
              className="group block bg-[#0a0f14] border border-[#80edfa]/20 hover:border-[#80edfa]/50 p-8 lg:p-12 transition-all duration-300 fade-in-up"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">[07]</p>
                  </div>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#f5f5f8] font-medium mb-3 group-hover:text-[#80edfa] transition-colors duration-300">Penetration Testing</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">Web · API · Infrastructure · Mobile</p>
                  <p className="text-sm text-[#666] leading-relaxed max-w-[680px] group-hover:text-[#aaa] transition-colors duration-300">
                    Senior-led manual penetration testing across web applications, APIs, infrastructure, and mobile. Business-logic vulnerabilities and chained exploits that automated scanners consistently miss. Remediation retesting included as standard.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Explore service</span>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none">
                    <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </Link>
          </div>

          {/* AI Pentesting, specialisation card */}
          <div className="mt-px">
            <Link
              href="/services/ai-pentesting"
              className="group block bg-[#050a0f] border border-[#222] hover:border-[#80edfa]/30 p-8 lg:p-12 transition-all duration-300 fade-in-up"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">[08]</p>
                    <span className="font-mono-brand text-[0.6rem] text-[#80edfa] border border-[#80edfa]/40 px-2 py-0.5 uppercase tracking-widest">Specialisation</span>
                  </div>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#f5f5f8] font-medium mb-3 group-hover:text-[#80edfa] transition-colors duration-300">AI Penetration Testing</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">LLM · RAG · Agentic · Model security</p>
                  <p className="text-sm text-[#666] leading-relaxed max-w-[680px] group-hover:text-[#aaa] transition-colors duration-300">
                    Adversarial security testing of AI and ML systems, prompt injection, RAG pipeline attacks, model extraction, and agentic AI exploitation. Purpose-built for organisations building or deploying AI.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Explore specialisation</span>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none">
                    <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </Link>
          </div>

          {/* Programmes section */}
          <div className="mt-16 mb-12 fade-in-up">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#80edfa] animate-pulse" />
              <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Programmes</p>
            </div>
            <p className="text-[#555] text-sm max-w-[560px]">Structured engagements designed to deliver a specific outcome, from rapid audit readiness to bespoke security programme design.</p>
          </div>

          {/* Accelerator callout, full width */}
          <div className="mb-px">
            <Link
              href="/services/accelerator"
              className="group block bg-[#0a0f14] border border-[#80edfa]/20 hover:border-[#80edfa]/50 p-8 lg:p-12 transition-all duration-300 fade-in-up"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">[A]</p>
                    <span className="font-mono-brand text-[0.6rem] text-[#80edfa] border border-[#80edfa]/40 px-2 py-0.5 uppercase tracking-widest">Programme spotlight</span>
                  </div>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#f5f5f8] font-medium mb-3 group-hover:text-[#80edfa] transition-colors duration-300">Accelerator Programme</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">Audit-ready · Fast · Embedded</p>
                  <p className="text-sm text-[#666] leading-relaxed max-w-[680px] group-hover:text-[#aaa] transition-colors duration-300">
                    When you need to get your security posture up to speed quickly, ahead of an audit, a funding round, or a major customer onboarding. Senior engineers embedded directly into your organisation, doing the work at pace. Typically 6–12 weeks, fully scoped to your situation.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Explore programme</span>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none">
                    <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </Link>
          </div>

          {/* Remaining programmes, 3 col grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#222]">
            {programmes?.slice(1)?.map((p, i) => (
              <Link
                key={i}
                href={p?.href}
                className="group bg-[#050a0f] p-8 lg:p-10 flex flex-col justify-between min-h-[280px] hover:bg-[#0a0f14] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.08}s` }}
              >
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">{p?.tag}</p>
                    <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 transform" viewBox="0 0 16 16" fill="none">
                      <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 className="font-heading text-xl lg:text-2xl text-[#f5f5f8] font-medium mb-2 group-hover:text-[#80edfa] transition-colors duration-300">{p?.title}</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">{p?.subtitle}</p>
                  <p className="text-sm text-[#666] leading-relaxed group-hover:text-[#aaa] transition-colors duration-300">{p?.description}</p>
                </div>
                <div className="mt-8 flex items-center gap-2">
                  <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Learn more</span>
                  <svg className="w-3 h-3 text-[#444] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators strip */}
      <section className="border-t border-[#222] py-16 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: 'Embedded', desc: 'Part of your team, not an external vendor' },
              { label: 'Continuous', desc: 'Security coverage every day of the year' },
              { label: 'Risk-led', desc: 'Prioritised by real business risk' },
              { label: 'Senior only', desc: 'No juniors, no automated-only outputs' },
            ]?.map((d, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-4" />
                <p className="font-heading text-lg text-[#f5f5f8] font-medium mb-2">{d?.label}</p>
                <p className="text-sm text-[#555]">{d?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Not sure which service fits your situation?
            </h2>
            <p className="text-[#aaa] text-sm mt-2">Talk to a TechSonar engineer, we will scope the right engagement for your organisation.</p>
          </div>
          <div className="fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <Link href="/contact" className="btn-primary">Book a scoping call</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
